import type { User } from '@features/auth/auth-client-adapter'
import { Link } from '@features/link'
import {
  ORGANIZATION_NUM_CLIENTS_OPTIONS,
  type OrganizationSignUpMessage,
  type OrganizationSignUpState,
  PRACTICE_MANAGEMENT_APP_OPTIONS
} from '@features/organizations/organization-sign-up-module'
import type {
  OrganizationSignUpInput,
  SignUpFlowId
} from '@features/organizations/organization-sign-up-flow'
import { organizationSignUpFlow } from '@features/organizations/organization-sign-up-flow'
import { TAX_APP_OPTIONS } from '@st/tax-folder'
import { Button, FullPageCard, Select, TextInput } from '@st/theme'
import { type ReactNode, useState } from 'react'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { NODE_ENV } from '~/env'
import { OrganizationSelectorPage } from './organization-selector'

// A local test account accepts no legal agreement.
const TERMS_REQUIRED = NODE_ENV != 'development'

const INITIAL_FORM: OrganizationSignUpInput = {
  userName: '',
  userEmail: '',
  organizationName: '',
  phoneNumber: '',
  password: '',
  passwordConfirm: '',
  tosPrivacyAgreement: false,
  taxApp: undefined
}

type Props = {
  header?: ReactNode
  onClickLogin?: () => void
  flow?: SignUpFlowId
}

export function OrganizationSignUpPage({ header, onClickLogin, flow }: Props) {
  const organizationSignUp = useProcess('organizationSignUp')
  const state = useSelect('organizationSignUp', (state) => state)

  return (
    <OrganizationSignUpScreen
      header={header}
      onClickLogin={onClickLogin}
      flow={flow}
      state={state}
      send={organizationSignUp.send}
    />
  )
}

export function OrganizationSignUpScreen({
  header,
  onClickLogin,
  flow,
  state,
  send
}: Props & {
  state: OrganizationSignUpState
  send: (message: OrganizationSignUpMessage) => void
}) {
  const [input, setInput] = useState<OrganizationSignUpInput>(INITIAL_FORM)
  const flowDefinition = organizationSignUpFlow(flow)

  return match(state)
    .with({ status: 'resolvingAuth' }, () => <FullPageCard header={header} />)
    .with({ status: 'selectingOrganization' }, () => (
      <OrganizationSelectorPage
        onClickCreateOrganization={() => send({ type: 'createOrganizationSelected' })}
      />
    ))
    .with(
      {
        status: P.union(
          'enteringAccountDetails',
          'enteringOrganizationDetails',
          'creatingUser',
          'creatingOrganization',
          'finishing'
        )
      },
      (state) => (
        <OrganizationSignUpForm
          header={header}
          onClickLogin={onClickLogin}
          flow={flowDefinition}
          state={state}
          input={input}
          setInput={setInput}
          onSubmit={() => send({ type: 'submit', input, termsRequired: TERMS_REQUIRED, flow })}
        />
      )
    )
    .exhaustive()
}

type OrganizationSignUpFormState = Exclude<
  OrganizationSignUpState,
  { status: 'resolvingAuth' | 'selectingOrganization' }
>

function OrganizationSignUpForm({
  header,
  onClickLogin,
  flow,
  state,
  input,
  setInput,
  onSubmit
}: {
  header?: ReactNode
  onClickLogin?: () => void
  flow: ReturnType<typeof organizationSignUpFlow>
  state: OrganizationSignUpFormState
  input: OrganizationSignUpInput
  setInput: (input: OrganizationSignUpInput) => void
  onSubmit: () => void
}) {
  const error = match(state)
    .with(
      { status: P.union('enteringAccountDetails', 'enteringOrganizationDetails') },
      (state) => state.error
    )
    .with({ status: P.union('creatingUser', 'creatingOrganization', 'finishing') }, () => undefined)
    .exhaustive()
  const validationErrors = match(error)
    .with({ type: 'validation' }, ({ errors }) => errors)
    .otherwise(() => ({}))
  const currentUser = currentUserForForm(state)
  const schema = match(currentUser)
    .with(undefined, () => flow.forms(TERMS_REQUIRED).loggedOutSchema)
    .otherwise(() => flow.forms(TERMS_REQUIRED).loggedInSchema)
  const signingUp = match(state)
    .with({ status: P.union('creatingUser', 'creatingOrganization', 'finishing') }, () => true)
    .with({ status: P.union('enteringAccountDetails', 'enteringOrganizationDetails') }, () => false)
    .exhaustive()
  const tosPrivacyAgreementId = 'organization-sign-up-tos-privacy-agreement'

  return (
    <FullPageCard header={header}>
      <h1 className="w-max-500 text-center text-3xl">{flow.headline}</h1>

      <div className="text-center text-base">
        {'Already have an account?'} <LoginLink onClick={onClickLogin} />
      </div>

      <form className="w-max-[600px] mt-4">
        <div className="flex flex-col items-stretch gap-4">
          <InputField
            label="Your name"
            ariaLabel="Full name"
            value={input.userName}
            error={validationErrors.userName}
            onChange={(value) => setInput({ ...input, userName: value })}
          />

          <InputField
            label="Company name"
            value={input.organizationName}
            error={validationErrors.organizationName}
            onChange={(value) => setInput({ ...input, organizationName: value })}
          />

          <InputField
            label="Email"
            type="text"
            value={input.userEmail}
            error={validationErrors.userEmail}
            onChange={(value) => setInput({ ...input, userEmail: value })}
          />

          {hasField(schema, 'phoneNumber') && (
            <InputField
              label="Phone number (optional)"
              type="text"
              value={input.phoneNumber}
              error={validationErrors.phoneNumber}
              onChange={(value) => setInput({ ...input, phoneNumber: value })}
            />
          )}

          {hasField(schema, 'password') && (
            <InputField
              label="Create password"
              type="password"
              value={input.password}
              error={validationErrors.password}
              onChange={(value) => setInput({ ...input, password: value })}
            />
          )}

          {hasField(schema, 'passwordConfirm') && (
            <InputField
              label="Confirm password"
              type="password"
              value={input.passwordConfirm}
              error={validationErrors.passwordConfirm}
              onChange={(value) => setInput({ ...input, passwordConfirm: value })}
            />
          )}

          {hasField(schema, 'taxApp') && (
            <div className="flex flex-col items-stretch gap-1.5">
              <Select
                ariaLabel="Tax software"
                options={TAX_APP_OPTIONS}
                placeholder="Select your tax software"
                value={input.taxApp}
                onChange={(value) => setInput({ ...input, taxApp: value })}
                buildValue={(o) => o.key}
                buildLabel={(o) => o.label}
              />
              {validationErrors.taxApp && (
                <div className="text-base text-red-600">{validationErrors.taxApp}</div>
              )}
            </div>
          )}

          {hasField(schema, 'numberOfClients') && (
            <div className="flex flex-col items-stretch gap-1.5">
              <Select
                ariaLabel="Number of clients"
                options={ORGANIZATION_NUM_CLIENTS_OPTIONS}
                placeholder="Number of clients"
                value={input.numberOfClients}
                onChange={(value) => setInput({ ...input, numberOfClients: value })}
                buildValue={(o) => o}
                buildLabel={(o) => o}
              />
              {validationErrors.numberOfClients && (
                <div className="text-base text-red-600">{validationErrors.numberOfClients}</div>
              )}
            </div>
          )}

          {hasField(schema, 'practiceManagementApp') && (
            <div className="flex flex-col items-stretch gap-1.5">
              <Select
                ariaLabel="Practice management platform"
                options={PRACTICE_MANAGEMENT_APP_OPTIONS}
                placeholder="Practice management platform"
                value={input.practiceManagementApp}
                onChange={(value) => setInput({ ...input, practiceManagementApp: value })}
                buildValue={(o) => o.key}
                buildLabel={(o) => o.label}
              />
              {validationErrors.practiceManagementApp && (
                <div className="text-base text-red-600">
                  {validationErrors.practiceManagementApp}
                </div>
              )}
            </div>
          )}

          {error?.type == 'general' && (
            <div className="text-base text-red-600">{error.message}</div>
          )}

          <Button size="l" variant="primary" disabled={signingUp} onClick={onSubmit}>
            {match({ signingUp, environment: NODE_ENV })
              .with({ signingUp: true }, () => 'Creating account...')
              .with({ environment: 'development' }, () => 'Create test account')
              .otherwise(() => flow.submitLabel)}
          </Button>

          {match(NODE_ENV)
            .with('development', () => (
              <p className="text-sm">
                Local test environment. Creating a test account does not accept any legal agreement.
              </p>
            ))
            .otherwise(() => (
              <div className="flex flex-col gap-1.5">
                <div className="flex flex-row items-center gap-1.5 text-sm">
                  <input
                    id={tosPrivacyAgreementId}
                    type="checkbox"
                    className="size-4"
                    checked={input.tosPrivacyAgreement}
                    aria-label="Agree to StanfordTax Terms of Service and Privacy Policy"
                    onChange={(e) => setInput({ ...input, tosPrivacyAgreement: e.target.checked })}
                  />
                  <label htmlFor={tosPrivacyAgreementId}>
                    {`I agree to StanfordTax's `}
                    <a
                      className="text-blue-500"
                      target="_blank"
                      href="https://stanfordtax.com/terms-of-service"
                    >
                      Terms of Service
                    </a>
                    {' and '}
                    <a
                      className="text-blue-500"
                      target="_blank"
                      href="https://stanfordtax.com/privacy-policy"
                    >
                      Privacy Policy
                    </a>
                  </label>
                </div>

                {validationErrors.tosPrivacyAgreement && (
                  <div className="text-base text-red-600">{validationErrors.tosPrivacyAgreement}</div>
                )}
              </div>
            ))}
        </div>
      </form>
    </FullPageCard>
  )
}

function hasField(
  schema: { fields: Readonly<Partial<Record<keyof OrganizationSignUpInput, unknown>>> },
  field: keyof OrganizationSignUpInput
) {
  return field in schema.fields
}

function currentUserForForm(state: OrganizationSignUpFormState): User | undefined {
  return match(state)
    .with(
      { status: P.union('enteringOrganizationDetails', 'creatingOrganization', 'finishing') },
      ({ user }) => user
    )
    .with({ status: P.union('enteringAccountDetails', 'creatingUser') }, () => undefined)
    .exhaustive()
}

function LoginLink({ onClick }: { onClick?: () => void }) {
  if (onClick) {
    return (
      <a className="cursor-pointer text-blue-500" onClick={onClick}>
        Click here to login
      </a>
    )
  }
  return (
    <Link className="text-blue-500" href={{ name: 'login' }}>
      Click here to login
    </Link>
  )
}

type InputFieldProps = {
  type?: 'text' | 'email' | 'password'
  label: string
  ariaLabel?: string
  value?: string
  error?: string
  onChange: (value: string) => void
}
function InputField({ type = 'text', label, ariaLabel, value, error, onChange }: InputFieldProps) {
  return (
    <div className="flex flex-col items-stretch gap-1.5">
      <TextInput
        ariaLabel={ariaLabel ?? label}
        placeholder={label}
        type={type}
        className="condensed"
        autoComplete="off"
        value={value}
        onChange={onChange}
      />
      {error && <div className="text-base text-red-600">{error}</div>}
    </div>
  )
}
